import { Workers, KeldaRequestType } from '../dto/keldaEnums';
import { makeError } from './exception';
import { invariant } from './invariant';
import type { VdcpControllerInfo } from '../dto/keldaConfigPrimitives';
import type { KeldaInputFolder } from '../dto/keldaConfigDto';
import { KELDA_INDEXED_FOLDERS_BUCKET, KELDA_AVID_FOLDERS_BUCKET, KELDA_LIVE_FOLDERS_BUCKET } from '../dto/keldaConfigPrimitives';
import type { ExtendedItemDto } from '../dto/extendedItemDto';
import type { FileLocation } from '../dto/fileLocation';
import type { ItemArtefact } from '../dto/transferRequestDtos';

// Name of the file that must be present in the server folder for
// Kelda to start. For now, this file only acts as a marker indicating
// that the file system is correctly mounted
//
// Safety files with the same name are expected to present inside
// indexed folders working in full mirroring mode, to avoid treating
// as deletions scenarios where a mount is temporarily gone
export const SAFETY_FILE = '.mimir';

export const INDEXED_FOLDERS_BOOKKEEPING = `.${KELDA_INDEXED_FOLDERS_BUCKET}_bookkeeping`;
export const AVID_FOLDERS_BOOKKEEPING = `.${KELDA_AVID_FOLDERS_BUCKET}_bookkeeping`;


/**
 * Given a path to a file or directory, returns the
 * path to the parent folder without a trailing slash.
 * Examples:
 *
 * /a/b/c.mxf => /a/b
 * /a/b/foo/ => /a/b
 */
export function getParentFolder(folderPath: string): string {
  const clean = folderPath.endsWith('/')
    ? folderPath.substring(0, folderPath.length - 1)
    : folderPath;
  const pos = clean.lastIndexOf('/');
  if (pos === -1) return '';
  return clean.substring(0, pos);
}


/**
 * The Avid bookkeeping folder is used for 2 purposes:
 * 1) keeping the .lock files corresponding to the .mxf files
 * 2) doing the bookkeeping about Mimir items (remux info, aaf, etc)
 *
 * This folder is where all data related to point 2 will be stored.
 */
export const AVID_ITEM_FOLDER = 'mimir_items';

export function getAvidBookkeepingFolder(avidFolder: KeldaInputFolder): KeldaInputFolder {
  invariant(avidFolder.type === 'avidFolder' && avidFolder.bookkeepingFolder, 'Internal error');
  return {
    ...avidFolder,
    type: 'avidBookkeepingFolder',
    path: avidFolder.bookkeepingFolder,
    bookkeepingFolder: undefined
  };
}


export function getWorkerType(requestType: KeldaRequestType): Workers {
  switch (requestType) {
    case KeldaRequestType.PREPROCESSING: return Workers.KELDA_REQUEST_PREPROCESSOR;
    case KeldaRequestType.FOLDER_SCANNING: return Workers.KELDA_FOLDER_SCANNER;
    case KeldaRequestType.INGEST: return Workers.KELDA_INGESTER;
    case KeldaRequestType.CONFORM: return Workers.KELDA_CONFORM;
    case KeldaRequestType.BATCH_PROCESS: return Workers.KELDA_BATCH_PROCESSOR;
    case KeldaRequestType.CONFORM_DVR: return Workers.KELDA_CONFORM;
    case KeldaRequestType.LIVE: return Workers.KELDA_LAUNCHER;
    case KeldaRequestType.STORAGE_TRANSFER: return Workers.STORAGE_TRANSFER;
    case KeldaRequestType.ARCHIVE: return Workers.ARCHIVE;
    case KeldaRequestType.LAUNCHER: return Workers.KELDA_LAUNCHER;
    case KeldaRequestType.STREAMING: return Workers.KELDA_STREAMER;
    case KeldaRequestType.ROUTER_CONTROLLER: return Workers.KELDA_ROUTER_CONTROLLER;
    case KeldaRequestType.VDCP_CONTROLLER: return Workers.KELDA_VDCP_CONTROLLER;
    case KeldaRequestType.ELEMENTAL_COMMAND: return Workers.KELDA_ELEMENTAL_PROXY;
    case KeldaRequestType.RENDER_SEQUENCE: return Workers.KELDA_SEQUENCE_RENDERER;
  }
}

export const getBucketNameAndBlobKeyForKeldaItem = (itemId: string, source: FileLocation, suppressErrors = false) => {
  /*
    We expect the source path to follow the following pattern:

    /kelda/kelda-root/{bucketName}/{blobKey...}
  */
  const keldaRootPathPrefix = '/kelda/kelda-root/';
  if (!source.path.startsWith(keldaRootPathPrefix)) {
    if (suppressErrors) return;
    throw makeError('bad kelda ingest source path prefix', 'BadItemState', { info: { itemId, path: source.path } });
  }

  const keldaRootRelative = source.path.substr(keldaRootPathPrefix.length);
  const pos = keldaRootRelative.indexOf('/');
  if (pos == -1) {
    if (suppressErrors) return;
    throw makeError('bad kelda ingest source root relative path', 'BadItemState', { info: { itemId, keldaRootRelative } });
  }

  const bucketName = keldaRootRelative.substr(0, pos);
  const blobKey = keldaRootRelative.substr(pos + 1);
  return { bucketName, blobKey };
};


/**
 * Returns true if item has streaming media or was ingested using the 'Kelda live' folder.
*/
export function isStreamingOrInKeldaLiveFolder(item: Pick<ExtendedItemDto, 'media' | 'ingest'>) {
  return item.media?.hasOwnProperty('streaming') ||
    (item.ingest?.source?.scheme === 'kelda' && item.ingest?.source?.path.startsWith(`/kelda/kelda-root/${KELDA_LIVE_FOLDERS_BUCKET}/`));
}

/**
 * Returns true if kelda live item.
*/
export function isKeldaLiveItem(item: Pick<ExtendedItemDto, 'media' | 'ingest'>) {
  if (item.ingest?.source?.scheme !== 'kelda') return false;

  /**
   * The presence of the 'streaming' property means that we have a live item.
   * However, if the live ingest failed early enough, it could happen that
   * the streaming property has not been set yet, which is why we also
   * check the ingest path to see if it looks like a Kelda live folder.
   */
  return item.media?.hasOwnProperty('streaming') || item.ingest?.source?.path.startsWith(`/kelda/kelda-root/${KELDA_LIVE_FOLDERS_BUCKET}/`);
}


/**
 * Returns the ID of the given controller router.
 * This ID will be used to identify Kelda requests that need
 * be sent to this specific controller.
 */
export function getRouterControllerId(routerId: string, controllerHost: string, controllerPort: number) {
  return `${routerId}/${controllerHost}/${controllerPort}`;
}

/**
 * Returns the ID of the given VDCP controller.
 * This ID is used to identify Kelda requests that need
 * be sent to this specific controller.
 * This ID is also what we for the corresponding VDCP livesource use as the pipeline ID.
 */
export function getVdcpControllerId(controllerInfo: VdcpControllerInfo) {
  const i = controllerInfo;
  return `${i.host}/${i.port}/${i.videoPort}`;
}


export function getKeldaTransferRequestId(keldaId: string, itemId: string, storageFolderName: string, artefact: ItemArtefact, targetFileName: string = '') {
  return `${keldaId}:${itemId}:${storageFolderName}:${artefact}${targetFileName ? `:${targetFileName}` : ''}`;
}
